import { useCallback, useState } from "react";
import { DateTime } from "luxon";
import type { DefinedDateRange } from "@/pages/human-resources/time-management/components/date-navigator/monthly-date-navigator/DefinedDateRange.ts";

interface Controller {
  selectedRange: DefinedDateRange;
  changeRangeToStartAt: (date: Date) => void;
  changeRangeToEndAt: (date: Date) => void;
  leftDisplayDate: Date;
  rightDisplayDate: Date;
  displayPreviousMonth: () => void;
  displayNextMonth: () => void;
}

export default function useDualMonthPaneController(
  startDate: Date,
  endDate: Date,
): Controller {
  const [selectedRange, setSelectedRange] = useState<DefinedDateRange>({
    from: startDate,
    to: endDate,
  });

  const [leftDisplayDate, setLeftDisplayDate] = useState(startDate);

  const rightDisplayDate = DateTime.fromJSDate(leftDisplayDate)
    .plus({ month: 1 })
    .toJSDate();

  const changeRangeToStartAt = useCallback((date: Date) => {
    const from = date;
    const to = DateTime.fromJSDate(from)
      .plus({ month: 1 })
      .minus({ day: 1 })
      .toJSDate();
    setSelectedRange({ from, to });
  }, []);

  const changeRangeToEndAt = useCallback((date: Date) => {
    const toDateTime = DateTime.fromJSDate(date);
    const fromDateTime =
      toDateTime.daysInMonth === toDateTime.day ?
        toDateTime.set({ day: 1 })
      : toDateTime.minus({ month: 1 }).plus({ day: 1 });
    setSelectedRange({
      from: fromDateTime.toJSDate(),
      to: toDateTime.toJSDate(),
    });
  }, []);

  const displayPreviousMonth = useCallback(() => {
    setLeftDisplayDate(
      DateTime.fromJSDate(leftDisplayDate).minus({ month: 1 }).toJSDate(),
    );
  }, [leftDisplayDate]);

  const displayNextMonth = useCallback(() => {
    setLeftDisplayDate(
      DateTime.fromJSDate(leftDisplayDate).plus({ month: 1 }).toJSDate(),
    );
  }, [leftDisplayDate]);

  return {
    selectedRange,
    changeRangeToStartAt,
    changeRangeToEndAt,
    leftDisplayDate,
    rightDisplayDate,
    displayPreviousMonth,
    displayNextMonth,
  };
}
